import React from 'react';
import ScoreEvent from './ScoreEvent';
import { IEvent, VIEW } from '../../interfaces';
import APIService from '../../services/APIService';
import scoreEventStore from './ScoreEventStore';
import rootStore, { IMobxRootState } from '../../RootStore';
import KeyValueStore from '../../stores/KeyValueStore';
import CancelableEvents, {
    CANCELED_PROMISE_ERROR,
} from '../../utils/CancelableEvents';
import { inject, observer } from 'mobx-react';
import { canDeleteScore, canEditScore, canScore } from 'components/eventsFeed/eventUtils';
import { RouteComponentProps, withRouter } from 'components/withRouter';
import { FGStack, FGTile } from '@fg/design-system';
import EventsFeedDataProvider from 'components/eventsFeed/EventFeedDataProvider';
import GQLoader from '../GQLoader/GQLoader';
import {
    isShowMockClusterEvent,
    withMockCluster,
} from 'components/eventsFeed/clusterMock';
const scoreEventCountryStore = new KeyValueStore<number>(true, Number);
const scoreEventRiskStore = new KeyValueStore<number>(false, Number, false);
scoreEventRiskStore.setValue(1);

interface IScoreEventProviderState {
    event: IEvent;
    isLoadingArticle: boolean;
}

type Props = {
    canUserScore?: boolean;
    canUserEditScore?: boolean;
    canUserDeleteScore?: boolean;
    defaultEvent?: number;
} & RouteComponentProps<{ id: string }>;

@inject(({ UserStore, scoreEventStore, eventsFeedStore }: IMobxRootState) => {
    return {
        canUserScore: canScore(scoreEventStore.activeEvent, UserStore),
        canUserEditScore: canEditScore(scoreEventStore.activeEvent, UserStore),
        canUserDeleteScore: canDeleteScore(scoreEventStore.activeEvent, UserStore),
        defaultEvent: eventsFeedStore.defaultEvent,
    };
})
@observer
class ScoreEventProvider extends React.Component<
    Props,
    IScoreEventProviderState
> {
    private currentId: string = null;
    private cancelableEvents = new CancelableEvents();
    constructor(props: any) {
        super(props);
        this.state = {
            event: null,
            isLoadingArticle: false,
        };
    }
    public componentDidMount() {
        rootStore.chartStore.setState({ expanded: false });
        scoreEventCountryStore.overrideValues(
            ...rootStore.countryStore.currentCountryList
        );
        scoreEventRiskStore.overrideValues(1);
        rootStore.appStore.setCurrentDisplayView(VIEW.SCORE_EVENT);
        rootStore.eventsFeedStore.setcollapse(true);
        this.redirectToDefaultEventIfNeeded();
    }

    public componentDidUpdate(prevProps: Props) {
        if (
            !this.props.params.id &&
            this.props.defaultEvent &&
            this.props.defaultEvent !== prevProps.defaultEvent
        ) {
            this.redirectToDefaultEventIfNeeded();
        }
    }

    private redirectToDefaultEventIfNeeded() {
        if (!this.props.params.id && this.props.defaultEvent) {
            this.props.navigate(
                `/${VIEW.SCORE_EVENT}/${this.props.defaultEvent}`,
                { replace: true }
            );
        }
    }

    public componentWillUnmount() {
        scoreEventStore.setActiveEvent(null);
        this.cancelableEvents.cancelAll();
    }
    public render() {
        return (
            <FGTile fullHeight padding="none">
                <FGStack style={{ height: '100%', width: '100%' }} gap={4}>
                    {this.props.params.id ? (
                        <ScoreEvent
                            {...this.props}
                            canUserDeleteScore={this.props.canUserDeleteScore}
                            canUserEditScore={this.props.canUserEditScore}
                            canUserScore={this.props.canUserScore}
                            close={this.close}
                            getData={this.getData}
                            isLoadingArticle={this.state.isLoadingArticle}
                        />
                    ) : (
                        <GQLoader />
                    )}
                    <EventsFeedDataProvider
                        onEventsClick={id => {
                            this.props.navigate(`/${VIEW.SCORE_EVENT}/${id}`);
                        }}
                    />
                </FGStack>
            </FGTile>
        );
    }

    public getData = async (id: string) => {
        this.setState({ isLoadingArticle: true });
        try {
            this.currentId = id;
            if (id) {
                const event = await this.cancelableEvents.promise(() => {
                    return APIService.getScoreEvent(Number(id));
                });
                if (this.currentId !== id) {
                    return;
                }
                const eventToSet = isShowMockClusterEvent(
                    this.props.location.search
                )
                    ? withMockCluster(event)
                    : event;
                scoreEventStore.setActiveEvent(eventToSet);
            } else {
                scoreEventStore.setActiveEvent(null);
            }
        } catch (err) {
            if (
                err instanceof Error &&
                err.message !== CANCELED_PROMISE_ERROR
            ) {
                return;
            }
        } finally {
            if (this.currentId === id) {
                this.setState({ isLoadingArticle: false });
            }
        }
    };

    private close = () => {
        rootStore.risksStore.setMinimized(false);
        this.props.navigate(-1);
    };
}

export default withRouter(ScoreEventProvider);